import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Button, Offcanvas } from "react-bootstrap";
import { FaSlidersH } from "react-icons/fa";
import ReactSwitch from "react-switch";
import { getInitialStockSelection } from "./StocksOptions";
import {
  autoScrollSelectComponents,
} from "../Common/SelectMenuScroll";
import { formatCoTraderFilterLabel } from "./TickerUniverseOptions";

const COUNTRY_STORAGE_KEY = "default_country_stock";
const CATEGORY_STORAGE_KEY = "default_category_stock";
const SYMBOL_STORAGE_KEY = "default_symbol_stock";
const UPDATE_SPEED_STORAGE_KEY = "default_update_speed";
const DEFAULT_CATEGORY = "Equities";

const getCountryOptions = (stockOptions = {}) =>
  Object.keys(stockOptions).map((country) => ({
    value: country,
    label: formatCoTraderFilterLabel(country),
  }));

const getCategoryOptions = (stockOptions = {}, countryValue) =>
  Object.keys(stockOptions[countryValue] || {}).map((category) => ({
    value: category,
    label: formatCoTraderFilterLabel(category),
  }));

const getSymbolsForSelection = (stockOptions = {}, countryValue, categoryValue) => {
  const symbols = stockOptions[countryValue]?.[categoryValue];
  return Array.isArray(symbols) ? symbols : [];
};

const resolveCategoryForCountry = (stockOptions, countryValue, categoryValue) => {
  const categoryMap = stockOptions[countryValue] || {};
  if (categoryValue && categoryMap[categoryValue]) {
    return categoryValue;
  }
  if (categoryMap[DEFAULT_CATEGORY]) {
    return DEFAULT_CATEGORY;
  }
  return Object.keys(categoryMap)[0] || "";
};

const resolveUpdateSpeed = () => {
  const saved = Number(localStorage.getItem(UPDATE_SPEED_STORAGE_KEY));
  const value = [2, 3, 5].includes(saved) ? saved : 2;
  return { value, label: `${value}s` };
};

const getTheme = (isDark) =>
  isDark
    ? {
        pageBg: "#0B0F1A",
        surface: "#12172A",
        surface2: "#0F1424",
        border: "#232B4A",
        text: "#E8ECF8",
        muted: "#A6B0CF",
        primary: "#4C7DFF",
        primarySoft: "rgba(76,125,255,0.14)",
        btnOutlineBorder: "#2B3560",
        selectedBg: "#4C7DFF",
        selectedText: "#FFFFFF",
        menuShadow: "0 18px 40px rgba(0,0,0,0.45)",
      }
    : {
        pageBg: "#FFFFFF",
        surface: "#FFFFFF",
        surface2: "#FFFFFF",
        border: "#E9ECEF",
        text: "#0B1220",
        muted: "#5B6472",
        primary: "#0D6EFD",
        primarySoft: "rgba(13,110,253,0.10)",
        btnOutlineBorder: "#0D6EFD",
        selectedBg: "#0D6EFD",
        selectedText: "#FFFFFF",
        menuShadow: "0 14px 30px rgba(11,18,32,0.12)",
      };

const CoTraderStocksMobileFiltersBar = ({
  selectedTickerLabel,
  isDark,
  stockOptions = {},
  defaultSymbol = "",
  isDisabled = false,
  setSelectedSymbol,
  setUpdateSpeed,
  setSelectedTickerLabel,
  showSuggestions,
  setShowSuggestions,
  language,
  setLanguage,
  languageList,
  setLanguageKey,
}) => {
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const t = useMemo(() => getTheme(isDark), [isDark]);
  const initialSelection = useMemo(
    () => getInitialStockSelection(stockOptions, defaultSymbol),
    [defaultSymbol, stockOptions],
  );
  const [selectedCountry, setSelectedCountry] = useState(
    initialSelection?.country || null,
  );
  const [selectedCategory, setSelectedCategory] = useState(
    initialSelection?.category || null,
  );
  const [selectedSymbolOption, setSelectedSymbolOption] = useState(
    initialSelection?.symbol || null,
  );
  const [selectedUpdateSpeed, setSelectedUpdateSpeed] = useState(resolveUpdateSpeed);

  const countryOptions = useMemo(() => getCountryOptions(stockOptions), [stockOptions]);
  const categoryOptions = useMemo(
    () => getCategoryOptions(stockOptions, selectedCountry?.value),
    [selectedCountry?.value, stockOptions],
  );
  const symbolsForSelection = useMemo(
    () =>
      getSymbolsForSelection(
        stockOptions,
        selectedCountry?.value,
        selectedCategory?.value,
      ),
    [selectedCategory?.value, selectedCountry?.value, stockOptions],
  );
  const languageOptions = useMemo(
    () => languageList.map((lang) => ({ value: lang, label: lang })),
    [languageList],
  );
  const selectedLanguageOption = useMemo(
    () => languageOptions.find((option) => option.value === language) || null,
    [language, languageOptions],
  );
  const updateSpeedOptions = useMemo(
    () => [
      { value: 2, label: "2s" },
      { value: 3, label: "3s" },
      { value: 5, label: "5s" },
    ],
    [],
  );
  const selectPortalStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 99999 }),
      container: (base) => ({ ...base, color: t.text }),
      control: (base, state) => ({
        ...base,
        minHeight: "46px",
        borderRadius: "12px",
        backgroundColor: t.surface2,
        borderColor: state.isFocused ? t.primary : t.border,
        boxShadow: state.isFocused ? `0 0 0 3px ${t.primarySoft}` : "none",
        ":hover": { borderColor: state.isFocused ? t.primary : t.border },
      }),
      valueContainer: (base) => ({ ...base, color: t.text }),
      singleValue: (base) => ({ ...base, color: t.text }),
      placeholder: (base) => ({ ...base, color: t.muted }),
      input: (base) => ({ ...base, color: t.text }),
      menu: (base) => ({
        ...base,
        zIndex: 99999,
        backgroundColor: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: t.menuShadow,
      }),
      menuList: (base) => ({
        ...base,
        backgroundColor: t.surface,
        padding: 6,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? t.selectedBg
          : state.isFocused
          ? isDark
            ? "rgba(35,43,74,0.65)"
            : "rgba(13,110,253,0.08)"
          : t.surface,
        color: state.isSelected ? t.selectedText : t.text,
        borderRadius: 10,
        cursor: "pointer",
        ":active": {
          backgroundColor: state.isSelected ? t.selectedBg : t.primarySoft,
        },
      }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: t.border }),
      dropdownIndicator: (base) => ({ ...base, color: t.muted }),
      noOptionsMessage: (base) => ({ ...base, color: t.muted }),
    }),
    [isDark, t],
  );

  useEffect(() => {
    if (!initialSelection) {
      setSelectedCountry(null);
      setSelectedCategory(null);
      setSelectedSymbolOption(null);
      return;
    }

    setSelectedCountry((current) => current || initialSelection.country);
    setSelectedCategory((current) => current || initialSelection.category);
    setSelectedSymbolOption((current) => current || initialSelection.symbol);
  }, [initialSelection]);

  useEffect(() => {
    if (!selectedCountry?.value) {
      return;
    }

    const nextCategoryValue = resolveCategoryForCountry(
      stockOptions,
      selectedCountry.value,
      selectedCategory?.value,
    );

    if (nextCategoryValue && nextCategoryValue !== selectedCategory?.value) {
      setSelectedCategory({
        value: nextCategoryValue,
        label: formatCoTraderFilterLabel(nextCategoryValue),
      });
      localStorage.setItem(CATEGORY_STORAGE_KEY, nextCategoryValue);
      return;
    }

    const symbolIsValid = symbolsForSelection.some(
      (symbol) => symbol.value === selectedSymbolOption?.value,
    );

    if (!symbolIsValid) {
      setSelectedSymbolOption(symbolsForSelection[0] || null);
    }

    localStorage.setItem(COUNTRY_STORAGE_KEY, selectedCountry.value);
  }, [
    selectedCategory?.value,
    selectedCountry?.value,
    selectedSymbolOption?.value,
    stockOptions,
    symbolsForSelection,
  ]);

  useEffect(() => {
    if (selectedCategory?.value) {
      localStorage.setItem(CATEGORY_STORAGE_KEY, selectedCategory.value);
    }

    if (selectedSymbolOption?.value) {
      setSelectedSymbol(selectedSymbolOption.value);
      setSelectedTickerLabel(selectedSymbolOption.label);
      localStorage.setItem(SYMBOL_STORAGE_KEY, selectedSymbolOption.value);
    }

    if (selectedUpdateSpeed?.value) {
      setUpdateSpeed(selectedUpdateSpeed.value);
      localStorage.setItem(UPDATE_SPEED_STORAGE_KEY, String(selectedUpdateSpeed.value));
    }
  }, [
    selectedCategory,
    selectedSymbolOption,
    selectedUpdateSpeed,
    setSelectedSymbol,
    setSelectedTickerLabel,
    setUpdateSpeed,
  ]);

  const setSymbol = (symbol) => {
    setSelectedSymbolOption(symbol);
  };

  const handleUpdateSpeed = (speed) => {
    setSelectedUpdateSpeed(speed);
  };

  const summary = selectedCountry?.value
    ? `${selectedCountry.value} • ${selectedCategory?.value || "--"} • ${
        selectedUpdateSpeed.label
      }`
    : "Stock picker unavailable";

  return (
    <>
      <div
        className="px-3 pt-2 pb-2"
        style={{
          borderBottom: `1px solid ${t.border}`,
          background: t.surface,
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div style={{ minWidth: 0 }}>
            <div
              className="fw-semibold text-truncate"
              style={{ fontSize: "1.05rem", color: t.text }}
            >
              {selectedSymbolOption?.label || selectedTickerLabel || "Select"}
            </div>
            <div className="text-truncate" style={{ fontSize: "0.85rem", color: t.muted }}>
              {summary}
            </div>
          </div>

          <Button
            className="px-3 rounded-pill"
            onClick={() => setShowFilterMenu(true)}
            disabled={isDisabled || countryOptions.length === 0}
            style={{
              background: "transparent",
              border: `1px solid ${t.btnOutlineBorder}`,
              color: t.primary,
              whiteSpace: "nowrap",
            }}
          >
            <FaSlidersH className="me-2" />
            Filters
          </Button>
        </div>
      </div>

      <Offcanvas
        show={showFilterMenu}
        onHide={() => setShowFilterMenu(false)}
        placement="bottom"
        style={{
          height: "85%",
          background: t.pageBg,
          borderTop: `1px solid ${t.border}`,
          paddingBottom: "60px",
        }}
      >
        <Offcanvas.Header
          closeButton
          style={{ background: t.surface, borderBottom: `1px solid ${t.border}` }}
        >
          <Offcanvas.Title style={{ color: t.text }}>
            Filters & Settings
          </Offcanvas.Title>

          <style>{`
            .offcanvas-header .btn-close { filter: ${isDark ? "invert(1)" : "none"}; }
          `}</style>
        </Offcanvas.Header>

        <Offcanvas.Body style={{ background: t.pageBg, color: t.text }}>
          <div className="mb-2 fw-semibold" style={{ color: t.muted }}>
            Filters
          </div>

          <div className="mb-3">
            <div style={{ color: t.muted, fontSize: "0.85rem" }} className="mb-1">
              Country
            </div>
            <Select
              options={countryOptions}
              value={selectedCountry}
              onChange={setSelectedCountry}
              isDisabled={isDisabled || countryOptions.length === 0}
              menuPortalTarget={document.body}
              styles={selectPortalStyles}
              components={autoScrollSelectComponents}
            />
          </div>

          <div className="mb-3">
            <div style={{ color: t.muted, fontSize: "0.85rem" }} className="mb-1">
              Category
            </div>
            <Select
              options={categoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
              isDisabled={isDisabled || categoryOptions.length === 0}
              menuPortalTarget={document.body}
              styles={selectPortalStyles}
              components={autoScrollSelectComponents}
            />
          </div>

          <div className="mb-3">
            <div style={{ color: t.muted, fontSize: "0.85rem" }} className="mb-1">
              Symbol
            </div>
            <Select
              options={symbolsForSelection}
              value={selectedSymbolOption}
              onChange={setSymbol}
              isDisabled={isDisabled || symbolsForSelection.length === 0}
              menuPortalTarget={document.body}
              styles={selectPortalStyles}
              placeholder={symbolsForSelection.length ? "Select symbol" : "No symbols"}
              components={autoScrollSelectComponents}
            />
          </div>

          <div className="mb-3">
            <div style={{ color: t.muted, fontSize: "0.85rem" }} className="mb-1">
              Update speed
            </div>
            <Select
              options={updateSpeedOptions}
              value={selectedUpdateSpeed}
              onChange={handleUpdateSpeed}
              isDisabled={isDisabled}
              menuPortalTarget={document.body}
              styles={selectPortalStyles}
              components={autoScrollSelectComponents}
            />
          </div>

          <hr className="my-4" style={{ borderColor: t.border }} />

          <div className="mb-2 fw-semibold" style={{ color: t.muted }}>
            Settings
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="d-flex align-items-center" style={{ color: t.text }}>
              <i className="fa fa-lightbulb me-2" />
              Show Suggestions
            </span>
            <ReactSwitch
              onChange={() => setShowSuggestions(!showSuggestions)}
              checked={showSuggestions}
              height={20}
              width={40}
              offColor={isDark ? "#232B4A" : "#E9ECEF"}
              onColor={t.primary}
              uncheckedIcon={false}
              checkedIcon={false}
            />
          </div>

          <div className="d-flex align-items-center w-100 mb-3">
            <div className="me-2" style={{ color: t.text }}>
              Language:
            </div>
            <div className="flex-grow-1">
              <Select
                options={languageOptions}
                value={selectedLanguageOption}
                onChange={(option) => {
                  const lang = option?.value || "English";
                  localStorage.setItem("user_language", lang);
                  setLanguage(lang);
                  setLanguageKey((prev) => prev + 1);
                  setShowFilterMenu(false);
                }}
                menuPortalTarget={document.body}
                styles={selectPortalStyles}
                components={autoScrollSelectComponents}
              />
            </div>
          </div>

          <div className="d-grid gap-2 mt-4">
            <button
              className="btn rounded-pill"
              onClick={() => setShowFilterMenu(false)}
              style={{ background: t.primary, border: "none", color: "#fff" }}
            >
              Done
            </button>
            <button
              className="btn rounded-pill"
              onClick={() => setShowFilterMenu(false)}
              style={{
                background: isDark ? t.surface2 : "#F8F9FA",
                border: `1px solid ${t.border}`,
                color: t.text,
              }}
            >
              Close
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default CoTraderStocksMobileFiltersBar;
